/**
 * AuthShowcase — the "image" for the auth split-screen left panel.
 *
 * Rather than ship a flat marketing photo, this is a self-contained, on-brand
 * product preview: a floating "next session" card (mirroring the real student
 * dashboard hero) layered over a soft stat chip and an avatar stack. Built from
 * plain markup + brand tokens, so it stays crisp at any size, themes correctly,
 * and never 404s like a hosted image would.
 *
 * Purely decorative — hidden from assistive tech via `aria-hidden`.
 */
function Avatar({ initials, className = '' }) {
  return (
    <span
      className={
        'flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white ring-2 ring-brand-700 ' +
        className
      }
    >
      {initials}
    </span>
  );
}

export default function AuthShowcase() {
  return (
    /* The two floating chips are positioned against this box's PADDING, not
       with negative offsets that escape it. Escaping is what let the top chip
       ride up into the headline paragraph and the bottom chip sit over the
       card's "Today · 4:00 PM" row — a float that overlaps a neighbour's text
       reads as a layout fault, however deliberate the layering was. */
    <div aria-hidden="true" className="relative mx-auto w-full max-w-sm select-none pb-12 pt-14">
      {/* Soft glow behind the preview */}
      <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-brand-500/20 blur-3xl" />

      {/* Back chip — "this week" stat, peeking out top-right.
       *
       * The offset has to clear the main card by more than the chip's first
       * line, or the label gets sliced through its own x-height and the chip
       * reads as a rendering fault rather than a card behind a card. At top-0
       * inside the container's pt-14 the label and the figure clear the card;
       * only the progress bar tucks behind it. */}
      <div className="absolute -right-5 top-0 z-0 hidden rotate-6 rounded-2xl border border-white/20 bg-white/12 px-4 py-3 backdrop-blur-sm sm:block">
        <p className="text-2xs font-medium uppercase tracking-wider text-white/70">This week</p>
        <p className="font-display text-lg font-bold text-white">6.5h learning</p>
        <div className="mt-1 h-1.5 w-28 overflow-hidden rounded-full bg-white/15">
          <div className="h-full w-2/3 rounded-full bg-gold-gradient" />
        </div>
      </div>

      {/* Main "next session" card */}
      <div className="relative z-10 rotate-[-2deg] rounded-2xl bg-white p-5 shadow-elevated ring-1 ring-black/5">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-2.5 py-1 text-2xs font-semibold text-success-700 ring-1 ring-inset ring-success-100">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            Starts in 2h 15m
          </span>
          <span className="text-slate-300">···</span>
        </div>

        <h3 className="mt-3 font-display text-lg font-bold tracking-tight text-slate-900">
          Personal Statement Review
        </h3>

        <div className="mt-3 flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gradient text-2xs font-semibold text-white">
            JD
          </span>
          <span className="text-sm text-slate-500">
            with <span className="font-medium text-slate-700">Dr. James Doyle</span> · Oxford
          </span>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
          <span className="text-sm font-medium text-slate-500">Today · 4:00 PM</span>
          <span className="inline-flex h-9 items-center rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white shadow-soft">
            Join session
          </span>
        </div>
      </div>

      {/* Avatar stack — "your mentors", peeking out bottom-left.
       *
       * Deliberately indigo, not white/15: this chip sits at z-20, ON TOP of
       * the white session card, so a translucent white fill turned into a
       * milky blob and its white text vanished into the card behind it. A dark
       * fill keeps the text legible over both the card and the panel, which is
       * what lets the chip overlap at all. */}
      <div className="absolute bottom-0 -left-4 z-20 flex items-center gap-3 rounded-2xl border border-white/15 bg-brand-950/80 px-4 py-3 shadow-elevated backdrop-blur-sm">
        <div className="flex -space-x-2.5">
          <Avatar initials="RP" className="bg-brand-500" />
          <Avatar initials="EL" className="bg-gold-500" />
          <Avatar initials="JD" className="bg-navy-400" />
        </div>
        <span className="text-xs leading-tight text-white/75">
          <span className="block font-semibold text-white">3 mentors</span>
          matched to you
        </span>
      </div>
    </div>
  );
}
